import React, { useEffect, useRef, useState } from 'react';
import { Box, Grid } from '@mui/material';

import useReportingAndAnalyticsApi from '../../../../../../pages/reporting-and-analytics/useReportingAndAnalyticsApi';
import useLoader from '../../../../../../../shared/components/loading-dialog/useLoader.hook';
import IDataSourceProps from '../../../../../../../shared/contracts/IDataSourceProps';
import LoadingDialogComponent from '../../../../../../../shared/components/loading-dialog/loading-dialog.component';
import WizardDataSourceComponent from './components/wizard-report-data-source/wizard-data-source.component';
import { StepperEnum, useWizardStepperContext } from '../../../../contexts/useWizardStepper.context';
import useWizardReportDataContext from '../../../../contexts/useWizardReportData.context';
import CallOutComponent from '../../../callout/callout.component';


/**
 * @author Pradeep R <pradeep.rajendran@rldatix.com>
 * @createdOn Dec/01/2024
 */
export default function WizardReportDataSourcesComponent({ }: {}) {

    const { activeStep, activeStepIndex, validateStep, setError } = useWizardStepperContext();

    const { isEditReport, wizardData, applyReportData,
        availableDataSources, cacheAvailableDataSources,
    } = useWizardReportDataContext();

    const addNewComptRef = useRef<HTMLDivElement | null>(null); // Ref for "Add New"
    const { getDatasourceList } = useReportingAndAnalyticsApi();

    const [selectedList, setSelectedList] = useState<IDataSourceProps[]>([]);
    const [errorMsg, setErrorMsg] = useState<string | null>(null);

    // TODO: show alert if the selectedList don't include datasource(s) from wizardData.datasourcesGuids 
    useEffect(() => {
        if (activeStep === StepperEnum.DataSources) {
            const isValid = selectedList?.length > 0;
            validateStep(activeStepIndex, isValid);

            if (isValid) {
                setError(activeStepIndex, null);
                setErrorMsg(null);
            } else {
                setError(activeStepIndex, 'Please select a data source.');
                setErrorMsg('Please select a data source.');
            }
        }
        //---

    }, [activeStep, selectedList]);

    useEffect(() => {
        if (availableDataSources?.length > 0 && wizardData?.datasourceGuids?.length > 0) {
            const itemsToSelect = availableDataSources.filter(x => wizardData.datasourceGuids.includes(x.dataSourceCode));
            if (itemsToSelect?.length > 0) {
                setSelectedList(itemsToSelect);
            }
        }
    }, [availableDataSources, wizardData]);

    const datasourcesLoader = useLoader({
        loaderName: 'Load Data Sources',
        loadAsync: () => getDatasourceList(
            {
                doIf: () => datasourcesLoader.isNotLoading,
                doBefore: () => {
                    cacheAvailableDataSources([]);
                    setSelectedList([]);
                    datasourcesLoader.SET_LOADING('Loading Data Sources...')
                },
                onFulfilled: (data) => {
                    datasourcesLoader.SET_SUCCESS('Success.!');
                    let normalized = (data || []).map(x => ({
                        ...x,
                        dataSourceCode: x.dataSourceCode.trim(),
                        dataSourceViewName: x.dataSourceViewName.trim()
                    }));
                    cacheAvailableDataSources([...normalized]);
                    scrollToAddNew();
                },
                onException: datasourcesLoader.SET_EXCEPTION
            })
    });


    useEffect(() => {
        datasourcesLoader.loadAsync();
    }, [])

    const handleSelectionChange = (
        prev: IDataSourceProps | null,
        next: IDataSourceProps | null
    ) => {
        let newSelectedList: IDataSourceProps[] = [...selectedList];

        if (prev) {

            // Find the index of the previous item
            const prevIndex = newSelectedList.findIndex(
                (x) => x.dataSourceCode === prev.dataSourceCode
            );

            if (prevIndex !== -1) {
                if (next) {  // Replace the previous item with the next item
                    newSelectedList[prevIndex] = next;
                } else { // If next is null, just remove the prev item
                    newSelectedList.splice(prevIndex, 1);
                }
            }
        } else if (next) { // Push the next item to the end of the list if no previous item exists
            newSelectedList.push(next);
        }

        // Update the selected list
        setSelectedList(newSelectedList);
        // TODO: fix here if datasources are allowed as an array
        if (newSelectedList?.length) {
            applyReportData({
                datasourceGuid: newSelectedList[0].dataSourceCode,
                datasourceGuids: [...newSelectedList.map(x => x.dataSourceCode)]
            })
        } else {
            applyReportData({
                datasourceGuid: '',
                datasourceGuids: []
            })
        }

        scrollToAddNew();
    };

    const scrollToAddNew = () => {
        setTimeout(() => {
            if (addNewComptRef.current) {
                addNewComptRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        }, 100)
    }

    return (<Box hidden={activeStep !== StepperEnum.DataSources} sx={{ px: 4, pt: 1, pb: 3 }}>
        {/* Loader Dialog */}
        {<LoadingDialogComponent loaderHook={datasourcesLoader} />}

        <Grid container direction={'column'} justifyItems='center' paddingBottom={4} sx={{ p: 1 }}>
            {isEditReport && <CallOutComponent type={'warning'} text={
                `Note: If you change or remove the original data source, the fields grid in the next step will be cleared. To restore, reset or enter manually.`} />}
        </Grid>

        <Grid container direction="row" spacing={3} sx={{ px: 2, pt: 3 }}>
            {/* Render each selected item */}
            {selectedList.map((item, index) => (
                <Grid item xs={4} key={`${item.dataSourceCode}_${index}`}>
                    <WizardDataSourceComponent
                        error={errorMsg}
                        count={index + 1}
                        defaultItem={item}
                        originalList={availableDataSources}
                        reservedList={selectedList}
                        onDataSourceChanged={handleSelectionChange}
                    />
                </Grid>
            ))}

            {/* Render the "add new" component only if no empty dropdown exists */}
            <Grid item xs={4} key="add-new-item">
                <WizardDataSourceComponent
                    error={errorMsg}
                    inputRef={addNewComptRef}
                    isAddNew={true}
                    defaultItem={null}
                    originalList={availableDataSources}
                    reservedList={selectedList}
                    onDataSourceChanged={handleSelectionChange}
                />
            </Grid>
        </Grid>

    </Box>);
}